// Contract of the Power admin route for the OPIN compliance watch: the operator's read-only
// status (last run, open findings, declared recertifications) and the recertification
// declaration body. The admin SPA depends on this contract only. `@finnest/compliance-watch`
// owns the domain vocabularies but also the node-only checks (x509, TLS), which must not reach
// the browser bundle, so the closed vocabularies are re-declared here and a parity test pins
// them to the owning package.
//
// The structs are closed contracts: decode them with `{ onExcessProperty: 'error' }`, so a member
// the contract does not define is refused rather than dropped.
import { Schema } from 'effect';
import { IsoDate, IsoDateTimeUtc } from '@finnest/core/effect';

const Count = Schema.Int.check(Schema.isGreaterThanOrEqualTo(0));
const MAX_KEY_CHARS = 300;
const MAX_TITLE_CHARS = 200;
const MAX_DETAIL_CHARS = 300;
const MAX_VARIANT_CHARS = 200;
// A finding set is bounded by the number of watched artefacts; one declaration per profile, four
// profiles.
export const COMPLIANCE_WATCH_FINDINGS_CAP = 500;
export const COMPLIANCE_WATCH_RECERTIFICATIONS_CAP = 4;

const boundedText = (max: number): Schema.String =>
  Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(max));

export const ComplianceCheckView = Schema.Literals([
  'material',
  'tls-edge',
  'directory',
  'recertification',
  'official-source',
]);
export const ComplianceSeverityView = Schema.Literals(['ok', 'attention', 'alert', 'critical']);
export const FindingLifecycleStateView = Schema.Literals(['open', 'indeterminate', 'resolved']);
export const RecertificationProfileView = Schema.Literals([
  'rp',
  'op-fapi',
  'op-dcr',
  'opin-journey-flow',
]);
export type RecertificationProfileView = typeof RecertificationProfileView.Type;
export const RunOutcomeView = Schema.Literals(['succeeded', 'failed', 'abandoned']);

/** How many findings the run left in each bucket; an indeterminate finding counts only there. */
export const RunTotalsView = Schema.Struct({
  ok: Count,
  attention: Count,
  alert: Count,
  critical: Count,
  indeterminate: Count,
});
export type RunTotalsView = typeof RunTotalsView.Type;

/**
 * `never-ran` carries no totals and no timestamps: a watch that has not run has nothing to
 * report, so there is no zeroed placeholder. `isStale` marks a last run older than the schedule
 * allows, so the page can say the status may be out of date.
 */
export const ComplianceWatchLastRun = Schema.Union([
  Schema.Struct({ kind: Schema.Literal('never-ran') }),
  Schema.Struct({
    kind: Schema.Literal('ran'),
    startedAt: IsoDateTimeUtc,
    finishedAt: IsoDateTimeUtc,
    outcome: RunOutcomeView,
    isStale: Schema.Boolean,
    totals: RunTotalsView,
  }),
]).annotate({
  title: 'PowerAdminComplianceWatchLastRun',
  description: 'The last compliance watch run, or that it never ran.',
});
export type ComplianceWatchLastRun = typeof ComplianceWatchLastRun.Type;

/** `detail` is pt-BR text rendered server-side; the SPA shows it as is. */
export const ComplianceFindingView = Schema.Struct({
  key: boundedText(MAX_KEY_CHARS),
  check: ComplianceCheckView,
  title: boundedText(MAX_TITLE_CHARS),
  severity: ComplianceSeverityView,
  state: FindingLifecycleStateView,
  detail: boundedText(MAX_DETAIL_CHARS),
  since: IsoDateTimeUtc,
});
export type ComplianceFindingView = typeof ComplianceFindingView.Type;

const DeclarationFields = {
  profileVariant: boundedText(MAX_VARIANT_CHARS),
  expiresAt: IsoDate,
};
const RecertificationFields = { profile: RecertificationProfileView, ...DeclarationFields };

export const RecertificationView = Schema.Union([
  Schema.Struct({ ...RecertificationFields, status: Schema.Literal('active') }),
  Schema.Struct({
    ...RecertificationFields,
    status: Schema.Literal('submitted'),
    submittedAt: IsoDate,
  }),
]).annotate({
  title: 'PowerAdminComplianceWatchRecertification',
  description:
    'An operator-declared certification (OIDF or OPIN journey flow) and whether its renewal was submitted.',
});
export type RecertificationView = typeof RecertificationView.Type;

export const ComplianceWatchStatusResponse = Schema.Struct({
  lastRun: ComplianceWatchLastRun,
  findings: Schema.Array(ComplianceFindingView).check(
    Schema.isMaxLength(COMPLIANCE_WATCH_FINDINGS_CAP),
  ),
  recertifications: Schema.Array(RecertificationView).check(
    Schema.isMaxLength(COMPLIANCE_WATCH_RECERTIFICATIONS_CAP),
  ),
}).annotate({
  title: 'PowerAdminComplianceWatchStatus',
  description:
    'Operator view of the OPIN compliance watch: last run, findings and declared recertifications.',
});
export type ComplianceWatchStatusResponse = typeof ComplianceWatchStatusResponse.Type;

/** Body of the declaration route; the profile comes from the path, the operator from the session. */
export const RecertificationDeclarationBody = Schema.Union([
  Schema.Struct({ ...DeclarationFields, status: Schema.Literal('active') }),
  Schema.Struct({
    ...DeclarationFields,
    status: Schema.Literal('submitted'),
    submittedAt: IsoDate,
  }),
]).annotate({
  title: 'PowerAdminRecertificationDeclarationBody',
  description: 'Operator declaration of an OIDF certification expiry and renewal submission.',
});
export type RecertificationDeclarationBody = typeof RecertificationDeclarationBody.Type;
